import { TOPIC_TYPE_TO_LABEL, type TopicType } from '@/queries/constants'

import type { FormInputType } from '@/components/UI/Form/types'

import composeRichText from '../richText/composeRichText'

export default function getCertificationPeerReviewFormInputs({
  topicName,
  topicType,
  courseName,
}: {
  topicName: string
  topicType: TopicType
  courseName: string
}): FormInputType[] {
  return [
    {
      label: 'Step 1: Watch 3 random test questions in depth',
      type: 'multi-checkbox',
      name: 'step1',
      options: [
        {
          label: 'Student always explains their reasoning out loud.',
          value: 'step1-explains-reasoning',
        },
        {
          label: `Student's reasoning sounds genuine and coherent, even if it is not always correct.`,
          value: 'step1-genuine-reasoning',
        },
        {
          label:
            'There are no indications of the student getting outside help (from a person, software or otherwise).',
          value: 'step1-no-outside-help',
        },
      ],
      size: 6,
    },
    {
      label: 'Step 2: Spot check throughout',
      type: 'multi-checkbox',
      name: 'step2',
      sublabel:
        'If you notice any other potential issues, please keep the last box unchecked and explain in the comments!',
      options: [
        {
          label:
            'Student does not wear headphones/earbuds, or anything that unnecessarily covers their face.',
          value: 'step2-no-headphones',
        },
        {
          label:
            'Student does not use any electronics other than their computer/recording device and a calculator.',
          value: 'step2-no-extra-electronics',
        },
        {
          label: `The student's whole screen is recorded, and they don't navigate to tabs/windows other than Schoolhouse.world, Khan Academy, and Desmos.`,
          value: 'step2-recording-whole-screen',
        },
        {
          label: `The student's whole face is shown at all times during the recording.`,
          value: 'step2-whole-face-shown',
        },
        {
          label: 'There are no other issues with this submission.',
          value: 'step2-no-other-issues',
        },
      ],
      size: 6,
    },
    {
      label: 'Step 3: Confirm key details',
      type: 'multi-checkbox',
      name: 'step3',
      options: [
        {
          label: composeRichText(ctx =>
            ctx.paragraph(
              'The Khan Academy assessment being worked on is called ',
              ctx.text(`${courseName} -- ${topicName}`, { bold: true }),
              ' and is a ',
              ctx.text(`${TOPIC_TYPE_TO_LABEL[topicType]}`, { bold: true }),
              '. You should confirm this by looking at the Khan Academy page in the video and the browser tab name.'
            )
          ),
          value: 'step3-confirm-assessment',
        },
      ],
      size: 6,
    },
    {
      type: 'number',
      name: 'num_correct',
      label: 'How many problems did the student answer correctly?',
      sublabel: 'If they scored 28/30 you would write 28.',
      placeholder: 'E.g. 28',
      required: true,
      size: 3,
    },
    {
      type: 'number',
      name: 'num_total',
      label: 'How many total problems were on the test?',
      sublabel: 'If they scored 28/30 you would write 30.',
      placeholder: 'E.g. 30',
      required: true,
      size: 3,
    },
    {
      label:
        'Please specify why you left (if any) boxes unchecked, including video timestamps when applicable.',
      type: 'textarea',
      name: 'feedback',
      placeholder: 'E.g. student was silent from 4:00-6:35',
      size: 6,
    },
    {
      type: 'multi-checkbox',
      name: 'confirmation',
      label: 'Honor Statement',
      options: [
        {
          label:
            'I have completed this peer review to the best of my ability based on the instructions provided. ' +
            'I understand that a pattern of negligent peer reviews would result in the flagging and possible ' +
            'suspension of my Schoolhouse account.',
          value: 'honor-confirmation',
        },
      ],
      required: true,
      size: 6,
    },
  ]
}
